<template>
  <form class="eligibility-tool__question" @submit.prevent="handleSubmit" novalidate>
    <h2 id="contextual-offers-dialog-title" ref="screenHeading" class="eligibility-tool__heading" tabindex="-1" data-modal-heading>{{ title }}</h2>
    <p v-if="helper" class="eligibility-tool__helper">{{ helper }}</p>
    <p v-if="error" class="eligibility-tool__error" role="alert">{{ error }}</p>
    <div id="contextual-offers-loading-status" class="sr-only" role="status" aria-live="polite">{{ isLoading ? 'Please wait' : '' }}</div>

    <div v-if="stepId === 'Q4'" class="eligibility-tool__lookup">
      <label class="eligibility-tool__label" for="contextual-offers-postcode-input">{{ question.label }}</label>
      <input
        id="contextual-offers-postcode-input"
        v-model.trim="postcode"
        class="eligibility-tool__input"
        type="text"
        autocomplete="postal-code"
        :disabled="skipPostcode"
        :aria-label="question.label"
        :aria-describedby="postcodeHintId"
        @input="handlePostcodeInput"
      />
      <span :id="postcodeHintId" class="eligibility-tool__hint eligibility-tool__hint--postcode">{{ question.placeholder }}</span>

      <label class="eligibility-tool__plain-option form-control" for="contextual-offers-q4-skip-postcode-checkbox">
        <input
          id="contextual-offers-q4-skip-postcode-checkbox"
          v-model="skipPostcode"
          class="eligibility-tool__plain-checkbox"
          type="checkbox"
          name="contextual-offers-postcode-skip"
          :aria-label="question.skipLabel"
          @change="handleSkipPostcodeChange"
        />
        <span class="eligibility-tool__radio-label">{{ question.skipLabel }}</span>
      </label>
    </div>

    <div v-if="stepId === 'Q5'" class="eligibility-tool__lookup">
      <label class="eligibility-tool__label" for="contextual-offers-school-input">{{ question.label }}</label>
      <input
        id="contextual-offers-school-input"
        v-model.trim="schoolQuery"
        class="eligibility-tool__input"
        type="text"
        autocomplete="off"
        :disabled="skipSchool"
        :aria-label="question.label"
        @input="handleSchoolInput"
      />
      <div
        v-if="schoolCountMessage"
        id="contextual-offers-school-results-count"
        class="eligibility-tool__status"
        role="status"
        aria-live="polite"
      >
        {{ schoolCountMessage }}
      </div>
      <div v-if="schoolQuery.length >= 2 && !schools.length && !selectedSchool && !skipSchool && searched" class="eligibility-tool__no-results-box" role="status">{{ question.noResults }}</div>
      <ul v-if="schools.length && !skipSchool" class="eligibility-tool__results" role="listbox" aria-label="School or college suggestions">
        <li v-for="school in schools" :key="school.id" class="eligibility-tool__result-option">
          <button :id="`contextual-offers-school-option-${sanitizeId(school.id)}`" type="button" class="eligibility-tool__result-button" :aria-label="`Select ${school.displayName}`" @click="selectSchool(school)">
            {{ school.displayName }}
          </button>
        </li>
      </ul>
      <label class="eligibility-tool__plain-option form-control" for="contextual-offers-q5-school-not-listed-checkbox">
        <input
          id="contextual-offers-q5-school-not-listed-checkbox"
          v-model="skipSchool"
          class="eligibility-tool__plain-checkbox"
          type="checkbox"
          name="contextual-offers-school-not-listed"
          :aria-label="question.skipLabel"
          @change="handleSkipSchoolChange"
        />
        <span>{{ question.skipLabel }}</span>
      </label>
    </div>

    <div class="eligibility-tool__actions button-group">
      <button
        :id="`contextual-offers-${stepId.toLowerCase()}-next-button`"
        class="button bg-bright-red eligibility-tool__button eligibility-tool__button--primary"
        type="submit"
        :disabled="isLoading"
        :aria-busy="isLoading ? 'true' : 'false'"
        :aria-describedby="isLoading ? 'contextual-offers-loading-status' : null"
      >
        <span v-if="isLoading" class="eligibility-tool__button-content">
          Please wait
          <span class="eligibility-tool__spinner" aria-hidden="true"></span>
        </span>
        <span v-else>Next</span>
      </button>
      <button :id="`contextual-offers-${stepId.toLowerCase()}-back-button`" class="button button--outline eligibility-tool__button eligibility-tool__button--secondary" type="button" @click="$emit('back')">Back</button>
    </div>
  </form>
</template>

<script>
import { mapState } from 'vuex';
import { questions } from '../../utils/decisionTree';
import { checkPostcode } from '../../services/postcodeService';
import { searchSchools } from '../../services/schoolLookupService';

export default {
  name: 'LookupQuestion',
  props: { stepId: { type: String, required: true } },
  data() {
    return {
      postcode: '',
      skipPostcode: false,
      isLoading: false,
      schoolQuery: '',
      schools: [],
      selectedSchool: null,
      skipSchool: false,
      error: '',
      searched: false,
      postcodeHintId: 'contextual-offers-postcode-hint'
    };
  },
  computed: {
    ...mapState(['answers']),
    question() { return questions[this.stepId]; },
    title() { return this.stepId === 'Q5' && this.answers.studyStatus === 'finished' ? questions.Q5.titleFinished : questions[this.stepId].title || questions.Q5.titleCurrent; },
    helper() { return this.question.helper; },
    schoolCountMessage() {
      if (this.stepId !== 'Q5' || this.skipSchool || !this.searched || this.selectedSchool) return '';
      const count = this.schools.length;
      return `${count} ${count === 1 ? 'school or college found' : 'schools or colleges found'}`;
    }
  },
  watch: {
    stepId() { this.restoreLocalState(); this.focusHeading(); },
    answers: {
      deep: true,
      handler() { this.restoreLocalState(); }
    }
  },
  mounted() {
    this.restoreLocalState();
    this.focusHeading();
  },
  methods: {
    focusHeading() {
      this.$nextTick(() => {
        const modalContent = this.$el.closest('.eligibility-tool__modal-content');
        if (modalContent) modalContent.scrollTo({ top: 0, left: 0 });
        if (this.$refs.screenHeading) this.$refs.screenHeading.focus({ preventScroll: true });
      });
    },
    restoreLocalState() {
      this.error = '';
      this.schools = [];
      this.searched = false;

      if (this.stepId === 'Q4') {
        const result = this.answers.postcodeResult || {};
        this.postcode = result.postcode || '';
        this.skipPostcode = Boolean(result.skipped);
  
      }

      if (this.stepId === 'Q5') {
        const result = this.answers.schoolResult || null;
        this.selectedSchool = result && !result.skipped ? result : null;
        this.skipSchool = Boolean(result && result.skipped);
        this.schoolQuery = this.selectedSchool ? this.selectedSchool.displayName : '';
      }
    },
    sanitizeId(value) { return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); },
    handlePostcodeInput() {
      this.skipPostcode = false;
      this.error = '';
    },
    handleSchoolInput() {
      this.skipSchool = false;
      this.search();
    },
    handleSkipPostcodeChange() {
      if (this.skipPostcode) {
        this.postcode = '';
        this.error = '';
      }
    },
    handleSkipSchoolChange() {
      if (this.skipSchool) {
        this.chooseSkipSchool();
      } else {
        this.error = '';
      }
    },
    async search() {
      this.error = '';
      this.selectedSchool = null;
      this.searched = false;
      if (this.schoolQuery.length < 2) { this.schools = []; return; }
      this.schools = await searchSchools(this.schoolQuery, this.answers.studyStatus);
      this.searched = true;
    },
    selectSchool(school) {
      this.selectedSchool = school;
      this.schoolQuery = school.displayName;
      this.schools = [];
      this.skipSchool = false;
      this.error = '';
    },
    chooseSkipSchool() {
      this.skipSchool = true;
      this.selectedSchool = null;
      this.schoolQuery = '';
      this.schools = [];
      this.searched = false;
      this.error = '';
    },
    async handleSubmit() {
      this.error = '';
      if (this.stepId === 'Q4') {
        if (!this.postcode && !this.skipPostcode) { this.error = questions.Q4.validationError; return; }
        if (this.skipPostcode) {
          this.$emit('continue', { key: 'postcode', result: { postcode: '', displayValue: 'Continue without a postcode', found: false, skipped: true, eligible: false, prefix: '', quintile: null } }); return;
        }

        try {
          this.isLoading = true;
          this.$emit('continue', { key: 'postcode', result: await checkPostcode(this.postcode) });
        } catch (error) {
          this.error = error.message || 'Please enter a valid postcode';
        } finally {
          this.isLoading = false;
        }
        return;
      }
      if (!this.selectedSchool && !this.skipSchool) { this.error = questions.Q5.validationError; return; }
      if (this.skipSchool) { this.$emit('continue', { key: 'school', result: { type: 'not-eligible', name: questions.Q5.summarySkipLabel, displayName: questions.Q5.summarySkipLabel, skipped: true } }); return; }
      this.$emit('continue', { key: 'school', result: this.selectedSchool });
    }
  }
};
</script>
